import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { Section, Layout, SEO } from '..';

import '../../../node_modules/react-image-gallery/styles/css/image-gallery.css';
import style from './GameParallelExperimentPage.module.less';

import { ParallelExperimentVideo } from './ParallelExperimentVideo';
import { SteamCard } from '../SteamCard';
import { NewsletterCard } from '../NewsletterCard';
import { AlbumImageGallery } from '../AlbumImageGallery/AlbumImageGallery';
import { FaqSection } from './FaqSection';

import lightsOn from './assets/lightsOn.jpg';
import lightsOff from './assets/lightsOff.jpg';
import whatIsPeBG from './assets/whatIsPeBG.jpg';
import logo from './assets/logo.png';
import metaImage from './assets/metaImage.jpg';

// screenshots
import screenshot1 from './assets/s1.jpg';
import screenshot2 from './assets/s2.jpg';
import screenshot3 from './assets/s3.jpg';
import screenshot4 from './assets/s4.jpg';
import screenshot5 from './assets/s5.jpg';
import screenshot6 from './assets/s6.jpg';
import screenshot7 from './assets/s7.jpg';
import screenshot8 from './assets/s8.jpg';
import screenshot9 from './assets/s9.jpg';
import screenshot10 from './assets/s10.jpg';
import screenshot11 from './assets/s11.jpg';
import screenshot13 from './assets/s13.jpg';

// game features
import border from './assets/border.png';
import mask from './assets/mask.png';
import coopGameplay from './assets/videos/CoopGameplay.webp';
import puzzlesForEveryone from './assets/videos/PuzzlesForEveryone.webp';
import questioningSuspects from './assets/videos/QuestioningSuspects.webp';
import environments from './assets/videos/Environments.webp';
import comic from './assets/videos/Comic.webp';
import knocking from './assets/videos/Knocking.webp';
import map from './assets/videos/Map.webp';
import drawOnAnything from './assets/videos/DrawOnAnything.webp';
import optionalChallenges from './assets/videos/OptionalChallenges.webp';

// game requirements & details
import clock from './assets/clock.png';
import comics from './assets/comics.png';
import padlock from './assets/padlock.png';
import { CtaSection } from '../CtaSection';



const screenshots = [
  { original: screenshot1 },
  { original: screenshot2 },
  { original: screenshot3 },
  { original: screenshot4 },
  { original: screenshot5 },
  { original: screenshot6 },
  { original: screenshot7 },
  { original: screenshot8 },
  { original: screenshot9 },
  { original: screenshot10 },
  { original: screenshot11 },
  { original: screenshot13 }
];

const EXPIRY_DATETIME = "2025-06-05T18:00:00";
const TARGET_LOCALE = "en-US";
const TARGET_TIMEZONE = "Europe/Warsaw";

export const GameParallelExperimentPage = () => {
  const [currentBackground, setCurrentBackground] = useState(lightsOn);
  const [backgroundBlinkAllowed, setBackgroundBlinkAllowed] = useState(true);
  const [count, setCount] = useState(0);

  const [expiryTime, setExpiryTime] = useState(new Date(EXPIRY_DATETIME).toLocaleString(TARGET_LOCALE, { timeZone: TARGET_TIMEZONE }));
  const [countdownTime, setCountdownTime] = useState({
    countdownDays: 0,
    countdownHours: 0,
    countdownMinutes: 0,
    countdownSeconds: 0,
  });

  const calculateCountdownTime = () => {
      const countdownDateTime = new Date(expiryTime).getTime();
      const currentDateTime = new Date(new Date().toLocaleString(TARGET_LOCALE, { timeZone: TARGET_TIMEZONE })).getTime();
      const remainingDayTime = countdownDateTime - currentDateTime;
      const totalDays = Math.floor(remainingDayTime / (1000 * 60 * 60 * 24));
      const totalHours = Math.floor(
        (remainingDayTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const totalMinutes = Math.floor(
        (remainingDayTime % (1000 * 60 * 60)) / (1000 * 60)
      );
      const totalSeconds = Math.floor(
        (remainingDayTime % (1000 * 60)) / 1000
      );

      const runningCountdownTime = {
        countdownDays: totalDays,
        countdownHours: totalHours,
        countdownMinutes: totalMinutes,
        countdownSeconds: totalSeconds,
      };

      setCountdownTime(runningCountdownTime);

      if (remainingDayTime < 0) {
        setExpiryTime("");
      }
  }

  const videoModalVisible = (videoModalVisible: boolean) => {
    setBackgroundBlinkAllowed(!videoModalVisible);
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setCount(count + 1);
      if (count === 1 || count % 5 === 0) {
        calculateCountdownTime();
      }
      if (backgroundBlinkAllowed) {
        setCurrentBackground(count % 20 === 0 || count % 25 === 0 ? lightsOff : lightsOn)
      }
    }, 200);
    return () => clearInterval(interval);
  }, [count]);

  return (
    <Layout transparentTop={true}>
      <SEO
        title="Parallel Experiment"
        description="Two detectives, one deadly experiment, and a race against time. In this 2-player co-op puzzle game, survival depends on teamwork and communication. Can you escape the parallel experiment together?"
        image={metaImage}
      />

      <Section className={style.welcomeCounter} alternate={true} straightTop={true} key={`${currentBackground}-img`} style={{ backgroundImage: `url(${currentBackground})` }}>
        <div className={style.wrapper}>
          <div className={style.counter}>
            {
              expiryTime &&
              <>
                <p>Parallel Experiment starts in:</p>
                <p>{countdownTime.countdownDays}d {countdownTime.countdownHours}h {countdownTime.countdownMinutes}m {countdownTime.countdownSeconds}s</p>
              </>
            }
            <ParallelExperimentVideo videoModalVisible={videoModalVisible} />
            <p>watch trailer</p>
          </div>
        </div>
      </Section>

      <Section className={style.storeCards}>
        <h1>Coming on June 5, 2025!</h1>
        <h4>Parallel Experiment is coming to Steam, App Store and Google Play.</h4>
        <div className={style.row}>
          <div className={style.content}>
            <SteamCard />
            <NewsletterCard tags={['parallel-experiment']} />
          </div>
        </div>
      </Section>

      <Section className={style.gameStory} alternate={true} style={{ backgroundImage: `url(${whatIsPeBG})`}}>
      <div className={style.content}>
        <div className={classNames(style.row, style.evenColumns)}>
          <div className={style.text}>
            <h1>What is</h1>
            <div className={style.logoImageContainer}>
              <img src={logo} />
            </div>
            <p>Parallel Experiment is a <strong>2-player cooperative puzzle game</strong> that separates players on different screens, requiring communication and teamwork to solve challenging puzzles. Players are often separated, each with different clues and items, making collaboration essential for solving the game’s escape-room-style challenges.</p>
            <p>Set in a noir-inspired world with a <strong>comic book art style</strong>, the game follows detectives Old Dog and Ally, who have become targets of the Cryptic Killer and are now unwilling participants in his twisted experiment. The gripping narrative, combined with unique NPC interactions, keeps the stakes high as players work together to stop the killer and escape his dangerous scheme.</p>
          </div>
          <div className={style.image} />
        </div>
      </div>
      </Section>

      <Section className={style.requirements} alternate={false}>
        <h1>Game requirements & details</h1>
        <div className={style.blocks}>
          <div className={style.block}>
            <img src={clock} alt="Clock" />
            <h3>5+ hours of gameplay</h3>
            <p>No fillers - every moment has a purpose.</p>
          </div>
          <div className={style.block}>
            <img src={comics} alt="Players" />
            <h3>Almost 100 comic book pages</h3>
            <p>...and over 10,000 in-game words, because a great story needs space to unfold.</p>
          </div>
          <div className={style.block}>
            <img src={padlock} alt="Age" />
            <h3>Nearly 80 puzzles</h3>
            <p>Carefully crafted to challenge your skills and keep the fun going.</p>
          </div>
        </div>
      </Section>

      <Section className={style.screenshots} alternate={true}>
        <h1>Game screenshots</h1>
        <AlbumImageGallery screenshots={screenshots} />
      </Section>

      <Section className={style.aboutParallelLab} alternate={false}>
        <h1>About Parallel Experiment</h1>
        <div className={style.row}>
          <div className={style.text}>
            <h2>Two-player cooperation</h2>
            <p>Parallel Experiment is designed specifically for <strong>two players</strong> that ofter are separated. Each puzzle is a meticulously crafted challenge that demands excellent communication and seamless teamwork. We’re pushing the boundaries of cooperative puzzle design to bring you a diverse array of puzzles that will surprise and delight you with their uniqueness.</p>
            <p>Remember, two detectives are better than one!</p>
          </div>
          <div className={style.image}>
            <div className={style.imageContainer}>
              <img
                className={style.maskedImage}
                src={coopGameplay}
                style={{ maskImage: `url(${mask})`}}
                alt="coopGameplay"
              />
              <img className={style.imageBorder} src={border} alt="imageBorder" />
            </div>
          </div>
        </div>

        <div className={classNames(style.row, style.alternate)}>
          <div className={style.text}>
            <h2>Puzzles for everybody</h2>
            <p>In Parallel Experiment, puzzles strike the perfect balance: <strong>challenging yet fair</strong>. Whether you're a first-time player or a seasoned puzzle enthusiast, there's something for everyone to enjoy.</p>
            <p>You’ll face an exciting variety of challenges, from redirecting water flows, finding computer passwords, and unlocking intricate locks, to deciphering cryptic ciphers, soldering electronic components, and even waking up a drunk! Dive into retro-inspired mini-games, master playful darts, and much more. Each puzzle is crafted to keep you engaged, testing your wits and creativity in unexpected ways.</p>
          </div>
          <div className={style.image}>
            <div className={style.imageContainer}>
              <img
                className={style.maskedImage}
                src={puzzlesForEveryone}
                style={{ maskImage: `url(${mask})`}}
                alt="puzzlesForEveryone"
              />
              <img className={style.imageBorder} src={border} alt="imageBorder" />
            </div>
          </div>
        </div>

        <div className={style.row}>
          <div className={style.text}>
            <h2>Questioning suspects</h2>
            <p>In Parallel Experiment, you’ll encounter characters who hold important information you need to obtain. But to get it, you have to steer conversations in meaningful ways. To achieve this, we developed a special <strong>cooperative dialogue system</strong>. NPCs will react not only to your actions and dialogue but also to your partner’s. By working together, you can achieve more, extract additional information, and receive help that wouldn’t be available if you were alone. Some conversations are puzzles in themselves that you need to solve together!</p>
          </div>
          <div className={style.image}>
            <div className={style.imageContainer}>
              <img
                className={style.maskedImage}
                src={questioningSuspects}
                style={{ maskImage: `url(${mask})`}}
                alt="questioningSuspects"
              />
              <img className={style.imageBorder} src={border} alt="imageBorder" />
            </div>
          </div>
        </div>

        <div className={classNames(style.row, style.alternate)}>
          <div className={style.text}>
            <h2>Hand-drawn environments</h2>
            <p>Immerse yourself in stunning, hand-drawn environments that feel like they’ve leapt straight off the <strong>pages of a comic book</strong>. From familiar settings to surprising locations, every scene is meticulously crafted to captivate your imagination.</p>
            <p>Each location is distinct, with its own atmosphere and details, making every step of your investigation a new discovery. Who knows what secrets await as you explore the unexpected twists of the story?</p>
          </div>
          <div className={style.image}>
            <div className={style.imageContainer}>
              <img
                className={style.maskedImage}
                src={environments}
                style={{ maskImage: `url(${mask})`}}
                alt="environments"
              />
              <img className={style.imageBorder} src={border} alt="imageBorder" />
            </div>
          </div>
        </div>

        <div className={classNames(style.row)}>
          <div className={style.text}>
            <h2>A story told in panels</h2>
            <p>Our love for comic books shines in Parallel Experiment. Every cutscene is presented as a beautifully crafted comic book page, immersing you in a gripping, noir-inspired narrative.</p>
            <p>How many pages did we create to tell the story? <strong>Almost 100 pages!</strong> Even we were surprised by how much it took, but every panel was worth it to deliver a story that keeps you on edge until the very last frame.</p>
          </div>
          <div className={style.image}>
            <div className={style.imageContainer}>
              <img
                className={style.maskedImage}
                src={comic}
                style={{ maskImage: `url(${mask})`}}
                alt="comic"
              />
              <img className={style.imageBorder} src={border} alt="imageBorder" />
            </div>
          </div>
        </div>

        <div className={classNames(style.row, style.alternate)}>
          <div className={style.text}>
            <h2>A little chaos, anyone?</h2>
            <p>Not a fan of cooperation? Or maybe you just want to have a little fun by <strong>annoying the other player</strong>? Parallel Experiment has you covered! You can poke, push, pinch, zap, play annoying sounds, or knock on glass to disrupt their progress in playful ways.</p>
            <p>Every level offers creative opportunities to keep your teammate on their toes. But remember, if you choose to use it, that’s entirely on you!</p>
          </div>
          <div className={style.image}>
            <div className={style.imageContainer}>
              <img
                className={style.maskedImage}
                src={knocking}
                style={{ maskImage: `url(${mask})`}}
                alt="knocking"
              />
              <img className={style.imageBorder} src={border} alt="imageBorder" />
            </div>
          </div>
        </div>

        <div className={classNames(style.row)}>
          <div className={style.text}>
            <h2>Exploring Krakow</h2>
            <p>For the first time in our series, Parallel Experiment takes you beyond a single location, allowing you to explore diverse areas across Krakow. Each site is packed with secrets to uncover, adding depth and variety to your investigation.</p>
            <p>To help you navigate this sprawling city, we’ve included a <strong>detailed in-game map</strong>. Whether you’re tracking clues or plotting your next move, the map is your essential guide to solving the mysteries that lie ahead.</p>
          </div>
          <div className={style.image}>
            <div className={style.imageContainer}>
              <img
                className={style.maskedImage}
                src={map}
                style={{ maskImage: `url(${mask})`}}
                alt="map"
              />
              <img className={style.imageBorder} src={border} alt="imageBorder" />
            </div>
          </div>
        </div>

        <div className={classNames(style.row, style.alternate)}>
          <div className={style.text}>
            <h2>Draw on... anything!</h2>
            <p>As a detective, taking notes is essential. That’s why Parallel Experiment gives you the freedom to <strong>draw on anything</strong> and everything in the game. Whether it’s jotting down clues, mapping out ideas, or... other creative uses, the in-game pen is always at your disposal.</p>
            <p>What you choose to draw first? Well, we have a pretty good guess.</p>
          </div>
          <div className={style.image}>
            <div className={style.imageContainer}>
              <img
                className={style.maskedImage}
                src={drawOnAnything}
                style={{ maskImage: `url(${mask})`}}
                alt="drawOnAnything"
              />
              <img className={style.imageBorder} src={border} alt="imageBorder" />
            </div>
          </div>
        </div>

        <div className={classNames(style.row)}>
          <div className={style.text}>
            <h2>Optional challenges for completionists</h2>
            <p>For those who love a challenge, Parallel Experiment offers <strong>optional puzzles</strong> designed to test your skills to the limit. As the story progresses, you’ll collect special coins that unlock unique challenges in the bar - perfect for blowing off steam or proving your puzzle-solving prowess.</p>
            <p>But that’s not all. Hidden within the bar lies a <strong>secret puzzle</strong> crafted by one of our amazing fans. Will you uncover it? And who knows what completing all the challenges might lead to?</p>
          </div>
          <div className={style.image}>
            <div className={style.imageContainer}>
              <img
                className={style.maskedImage}
                src={optionalChallenges}
                style={{ maskImage: `url(${mask})`}}
                alt="optionalChallenges"
              />
              <img className={style.imageBorder} src={border} alt="imageBorder" />
            </div>
          </div>
        </div>
      </Section>

      <FaqSection />

      <CtaSection />

      <img src={lightsOn} style={{display:'none'}} alt="lightsOn" />
      <img src={lightsOff} style={{display:'none'}} alt="lightsOff" />
    </Layout>
  )
};
